/**
 * Group Production Intervals into collapsible Production Track sections/rows.
 * Classification (section / producer) is written at parse-save — this file only
 * lays out stored fields (ADR 0014).
 */
import { costForEntity } from './components/sc2/replayUnitLookup';

const PRODUCER_ORDER = [
  'barracks',
  'factory',
  'starport',
  'gateway',
  'robotics_facility',
  'stargate',
  'command_center',
  'nexus',
  'hatchery',
  'lair',
  'hive',
  'larva',
];

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Tooltip text for a production bar (name, span, cost).
 */
export function formatIntervalTooltip(interval) {
  const name = interval?.name || 'Unknown';
  const start = formatClock(interval?.start_real_time);
  const finish = formatClock(interval?.finish_real_time);
  const cost = costForEntity(name, interval?.kind);
  const costPart =
    cost.minerals || cost.gas ? ` · ${cost.minerals}/${cost.gas}` : '';
  return `${name} ${start}–${finish}${costPart}`;
}

/**
 * Bar color class key for CSS.
 */
export function barClassForInterval(interval, rowKind) {
  if (interval.kind === 'worker') return 'worker';
  if (interval.kind === 'addon' || rowKind === 'addon') return 'addon';
  if (interval.kind === 'structure_morph' || rowKind === 'structure_morph') {
    return 'structure_morph';
  }
  if (rowKind === 'economy') return 'economy';
  if (rowKind === 'military') return 'military';
  if (interval.kind === 'army' || rowKind === 'producer') return 'army';
  if (interval.kind === 'building') return 'building';
  return 'building';
}

/**
 * Build collapsible sections with only non-empty rows.
 *
 * @param {Array<{ name?: string, kind?: string, section?: string, producer_id?: string, producer_label?: string, start_real_time?: number, finish_real_time?: number }>} intervals
 */
export function buildProductionTrackSections(intervals) {
  const list = intervals || [];
  const workers = [];
  const economy = [];
  const military = [];
  const addons = [];
  const structureMorphs = [];
  const otherBuildings = [];
  const byProducer = new Map();

  for (const interval of list) {
    const kind = interval.kind;
    const section = interval.section || '';
    if (kind === 'worker' || section === 'workers') {
      workers.push(interval);
      continue;
    }
    if (kind === 'addon' || section === 'addon') {
      addons.push(interval);
      continue;
    }
    if (kind === 'structure_morph' || section === 'structure_morphs') {
      structureMorphs.push(interval);
      continue;
    }
    if (kind === 'building') {
      if (section === 'economy') economy.push(interval);
      else if (section === 'military') military.push(interval);
      else if (section === 'addon') addons.push(interval);
      else otherBuildings.push(interval);
      continue;
    }
    if (kind === 'army' || section === 'army') {
      const producer = {
        id: interval.producer_id || 'other_units',
        label: interval.producer_label || 'Other units',
      };
      if (!byProducer.has(producer.id)) {
        byProducer.set(producer.id, { ...producer, intervals: [] });
      }
      byProducer.get(producer.id).intervals.push(interval);
      continue;
    }
    otherBuildings.push(interval);
  }

  const sections = [];

  if (workers.length > 0) {
    sections.push({
      id: 'workers',
      label: 'Workers',
      rows: [{ id: 'workers', label: 'Workers', rowKind: 'worker', intervals: workers }],
    });
  }

  const economyRows = [];
  if (economy.length > 0) {
    economyRows.push({
      id: 'economy',
      label: 'Economy',
      rowKind: 'economy',
      intervals: economy,
    });
  }
  if (economyRows.length > 0) {
    sections.push({ id: 'economy_buildings', label: 'Economy buildings', rows: economyRows });
  }

  if (structureMorphs.length > 0) {
    sections.push({
      id: 'structure_morphs',
      label: 'Structure morphs',
      rows: [
        {
          id: 'structure_morphs',
          label: 'Structure morphs',
          rowKind: 'structure_morph',
          intervals: structureMorphs,
        },
      ],
    });
  }

  const militaryRows = [];
  if (military.length > 0) {
    militaryRows.push({
      id: 'military',
      label: 'Military',
      rowKind: 'military',
      intervals: military,
    });
  }
  if (addons.length > 0) {
    militaryRows.push({
      id: 'addons',
      label: 'Addons',
      rowKind: 'addon',
      intervals: addons,
    });
  }
  if (otherBuildings.length > 0) {
    militaryRows.push({
      id: 'other_buildings',
      label: 'Other buildings',
      rowKind: 'other',
      intervals: otherBuildings,
    });
  }
  if (militaryRows.length > 0) {
    sections.push({
      id: 'military_buildings',
      label: 'Military buildings',
      rows: militaryRows,
    });
  }

  if (byProducer.size > 0) {
    const producerRows = Array.from(byProducer.values())
      .sort((a, b) => {
        const ai = PRODUCER_ORDER.indexOf(a.id);
        const bi = PRODUCER_ORDER.indexOf(b.id);
        const ao = ai === -1 ? PRODUCER_ORDER.length : ai;
        const bo = bi === -1 ? PRODUCER_ORDER.length : bi;
        if (ao !== bo) return ao - bo;
        return a.label.localeCompare(b.label);
      })
      .map((p) => ({
        id: p.id,
        label: p.label,
        rowKind: 'producer',
        intervals: p.intervals,
      }));
    sections.push({ id: 'producers', label: 'Army production', rows: producerRows });
  }

  return sections;
}
